import { createTheme, alpha } from '@mui/material/styles';
import { DURATION, EASING, ANIMATION_PRESETS } from './animations';

// Define custom color palette
const neutrals = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
};

const primary = {
  light: '#5B8AF5',
  main: '#3366FF',
  dark: '#1939B7',
};

const secondary = {
  light: '#9C92E1',
  main: '#6B59CC',
  dark: '#4A3D8F',
};

const success = {
  light: '#4ADE80',
  main: '#10B981',
  dark: '#047857',
};

const dashboardTheme = createTheme({
  palette: {
    primary: primary,
    secondary: secondary, 
    success: success,
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: neutrals[800],
      secondary: neutrals[600],
      disabled: neutrals[400],
    },
    divider: neutrals[200],
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.3rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: neutrals[500],
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.04)',
    '0px 2px 4px rgba(0, 0, 0, 0.04)',
    '0px 2px 8px rgba(0, 0, 0, 0.06)',
    '0px 4px 12px rgba(0, 0, 0, 0.06)',
    '0px 6px 16px rgba(0, 0, 0, 0.08)',
    '0px 8px 24px rgba(0, 0, 0, 0.08)',
    '0px 12px 32px rgba(0, 0, 0, 0.1)',
    '0px 16px 48px rgba(0, 0, 0, 0.1)',
    '0px 20px 64px rgba(0, 0, 0, 0.1)',
    '0px 24px 80px rgba(0, 0, 0, 0.12)',
    '0px 28px 96px rgba(0, 0, 0, 0.12)',
    '0px 32px 112px rgba(0, 0, 0, 0.12)',
    '0px 36px 128px rgba(0, 0, 0, 0.12)',
    '0px 40px 144px rgba(0, 0, 0, 0.14)',
    '0px 44px 160px rgba(0, 0, 0, 0.14)',
    '0px 48px 176px rgba(0, 0, 0, 0.14)',
    '0px 52px 192px rgba(0, 0, 0, 0.14)',
    '0px 56px 208px rgba(0, 0, 0, 0.16)',
    '0px 60px 224px rgba(0, 0, 0, 0.16)',
    '0px 64px 240px rgba(0, 0, 0, 0.16)',
    '0px 68px 256px rgba(0, 0, 0, 0.16)',
    '0px 72px 272px rgba(0, 0, 0, 0.18)',
    '0px 76px 288px rgba(0, 0, 0, 0.18)',
    '0px 80px 304px rgba(0, 0, 0, 0.18)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          padding: '8px 16px',
          fontWeight: 500,
          transition: `all ${DURATION.normal} ${EASING.easeOut}`,
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
          },
        },
        outlined: {
          borderColor: neutrals[300],
          '&:hover': {
            borderColor: neutrals[400],
            backgroundColor: alpha(neutrals[200], 0.2),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(neutrals[200], 0.2),
          },
        },
        textPrimary: {
          color: primary.main,
          '&:hover': {
            backgroundColor: alpha(primary.main, 0.05),
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${primary.main} 0%, ${primary.dark} 100%)`,
          transition: `all ${DURATION.normal} ${EASING.easeOut}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${primary.main} 10%, ${primary.dark} 90%)`,
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '10px 22px',
          fontSize: '0.9375rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)',
          borderRadius: 12,
          transition: `all ${DURATION.normal} ${EASING.easeOut}`,
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.08)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '20px 24px 12px',
        },
        title: {
          fontSize: '1rem',
          fontWeight: 600,
          color: neutrals[800],
          transition: `color ${DURATION.fast} ${EASING.easeInOut}`,
          '.MuiCard-root:hover &': {
            color: primary.main,
          },
        },
        subheader: {
          fontSize: '0.85rem',
          color: neutrals[500],
        },
        action: {
          marginTop: 0,
          marginRight: 0,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 28,
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            transform: 'scale(1.05)',
          },
        },
        filled: {
          backgroundColor: neutrals[200],
          color: neutrals[700],
          '&:hover': {
            backgroundColor: neutrals[300],
          },
        },
        outlined: {
          borderColor: neutrals[300],
          '&:hover': {
            borderColor: neutrals[400],
          },
        },
        deleteIcon: {
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            color: 'inherit',
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            backgroundColor: alpha(neutrals[200], 0.4),
            transform: 'scale(1.1)',
          },
        },
        sizeSmall: {
          padding: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutrals[200]}`,
          padding: '16px',
          fontSize: '0.875rem',
        },
        head: {
          backgroundColor: neutrals[50],
          color: neutrals[600],
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            backgroundColor: alpha(neutrals[100], 0.6),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(primary.light, 0.1),
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(primary.light, 0.2),
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutrals[200]}`,
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minWidth: 'auto',
          padding: '12px 16px',
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            color: primary.main,
            backgroundColor: alpha(primary.light, 0.05),
          },
          '&.Mui-selected': {
            color: primary.main,
            fontWeight: 600,
          },
          '&:not(.Mui-selected):hover': {
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutrals[800],
          color: '#fff',
          fontSize: '0.75rem',
          borderRadius: 4,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          padding: '8px 12px',
          fontWeight: 500,
        },
        arrow: {
          color: neutrals[800],
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          transition: `all ${DURATION.normal} ${EASING.easeOut}`,
          '&:hover': {
            backgroundColor: alpha(neutrals[100], 0.5),
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0 16px',
          '&.Mui-expanded': {
            minHeight: 48,
          },
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            transform: 'translateX(4px)',
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0 16px 16px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            backgroundColor: alpha(neutrals[100], 0.8),
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha(primary.light, 0.1),
            '&:hover': {
              backgroundColor: alpha(primary.light, 0.2),
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
          '&:hover': {
            backgroundColor: alpha(neutrals[100], 0.8),
            transform: 'translateX(4px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: neutrals[400],
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: primary.main,
                borderWidth: 2,
              },
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 8px ${alpha(neutrals[300], 0.25)}`,
            },
          },
        },
      },
    },
  },
});

export default dashboardTheme; 